import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output, ViewChild } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { finalize, Observable, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, startWith, switchMap } from 'rxjs/operators';
import { HttpParams } from '@angular/common/http';
import {
    MatAutocompleteActivatedEvent,
    MatAutocompleteSelectedEvent,
    MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { MatInput } from '@angular/material/input';
import { KieneBackendApiService } from '../../services/backend-api/kiene-backend-api.service';
import { MessageService } from '../../services/message-service/message.service';
import { API_BASE_URL_SERVICE } from '../../kiene-core.config';
import { Inject } from '@angular/core';

@Component({
    selector: 'kiene-autocomplete',
    templateUrl: './kiene-autocomplete.component.html',
    styleUrls: ['./kiene-autocomplete.component.scss'],
    host: { '(window:keydown)': 'checkForKeyShortcut($event)' },
})
export class KieneAutocompleteComponent implements OnChanges, AfterViewInit {
    @ViewChild('inputAutocomplete') inputAutocomplete: MatInput;
    @ViewChild(MatAutocompleteTrigger) auto: MatAutocompleteTrigger;

    formControl = new UntypedFormControl();
    isLoading = false;

    @Input('kieneObject') kieneObject: any;
    @Input('showLoadingIndicator') showLoadingIndicator = false;

    @Input('displayField') displayField = 'bezeichnung';

    @Input('displayFields') displayFields: string[] = [];

    @Input('mapperDisplayFields') mapperDisplayFields: string[] = [];

    @Input('separator') separator = ' ';

    @Input('apiUrl') apiUrl: string;

    @Input('genericApiUrl') genericApiUrl: string;

    @Input('params') params: HttpParams;

    @Input('placeholder') placeholder = 'Bezeichnung';

    @Input('panelWidth') panelWidth: string | number | null = null;

    @Input('panelClass') panelClass: string | string[] = [];

    @Input('focus') focus = true;

    @Input('presetSelected') presetSelected = true;

    @Input('disabled') disabled = false;

    @Input('required') required = false;

    @Input('clearAfterSelect') clearAfterSelect = false;

    @Input('resetValue') resetValue: string;

    @Input('showClear') showClear = true;

    @Input('limit') limit = 50;

    @Input('emitFreeText') emitFreeText = false;

    @Input('multiLine') multiLine = false;

    @Input('type') type = 'text';

    @Input('inputmode') inputmode = '';

    @Input('showDeleteButton') showDeleteButton = false;

    @Input('noPaddingNoMargin') noPaddingNoMargin = false;

    @Input('emitButton') emitButton: string;

    @Input('createNewAllowed') createNewAllowed = false;

    @Input('createNewText') createNewText = 'Neuen Antrag anlegen';

    @Input('openOnlyOnClick') openOnlyOnClick = false;

    @Output('elementSelected') elementSelected = new EventEmitter();

    @Output('deleteSelected') deleteSelected = new EventEmitter();

    @Output('optionActivated') optionActivated = new EventEmitter();

    @Output('createNew') createNewEmitter = new EventEmitter<string>();

    options: any[];

    optionSelected = false;

    activatedObject: any;
    activatedObjectSelected = false;

    createObj: any;
    private suppressNextAutoOpenAfterSelect = false;
    private openPanelAfterPointer = false;

    constructor(
        private readonly api: KieneBackendApiService,
        private readonly messageService: MessageService,
        private readonly cdr: ChangeDetectorRef,
        @Inject(API_BASE_URL_SERVICE) private readonly apiBaseUrl: string,
    ) {}

    ngOnChanges() {
        if (this.kieneObject) {
            this.formControl.setValue(this.kieneObject, { emitEvent: false }); // neu
        }

        if (this.disabled) {
            this.formControl.disable({ emitEvent: false }); // neu
        } else {
            this.formControl.enable({ emitEvent: false }); // neu
        }
    }

    ngAfterViewInit() {
        setTimeout(() => {
            this.autocomplete('ngAfterViewInit');
            if (this.focus) {
                this.inputAutocomplete?.focus();
            }
            if (this.kieneObject) {
                this.formControl.setValue(this.kieneObject);
                if (this.focus) {
                    this.inputAutocomplete?.focus();
                    if (this.presetSelected) {
                        // this.inputAutocomplete.nativ
                    }
                }
            }

            if (this.required) {
                this.formControl.setValidators(Validators.required);
            }

            if (this.disabled) {
                this.formControl.disable();
            } else {
                this.formControl.enable();
            }
        }, 1);
    }

    clear() {
        this.formControl.setValue('');
        this.activatedObjectSelected = false;
        this.elementSelected.emit(null);
        // this.autocomplete('clear');
        this.formControl.markAsUntouched();
    }

    deleteElement(event, option: any) {
        event.stopPropagation();
        this.options = [];
        this.deleteSelected.emit(option);
    }

    public clearAndFocus() {
        this.formControl.setValue('');
        this.activatedObjectSelected = false;
        this.elementSelected.emit(null);
        // this.autocomplete('clearAndFocus');
        this.inputAutocomplete?.focus();
        this.formControl.markAsUntouched();
    }

    public setValue(value: any, emit: boolean) {
        this.formControl.setValue(value, { emitEvent: emit });
        this.activatedObjectSelected = false;
    }

    autocomplete(sender: string) {
        this.formControl.valueChanges
            .pipe(
                debounceTime(250),
                distinctUntilChanged(),
                switchMap((value: string) => {
                    if (this.isObject(value)) {
                        // Selected option objects should not trigger another backend search,
                        // otherwise the panel can re-open immediately while input keeps focus.
                        this.options = [];
                        this.auto?.closePanel();
                        return of({ records: [] });
                    }
                    this.activatedObjectSelected = false;
                    this.isLoading = true;
                    return this.getSearchRequest(value).pipe(
                        finalize(() => (this.isLoading = false)),
                        catchError(err => {
                            this.messageService.errorMessage('Es ist ein Fehler aufgetreten!', err);
                            return of({ records: [] });
                        }),
                    );
                }),
            )
            .subscribe({
                next: response => {
                    this.options = response?.records ?? [];
                },
            });
    }

    /**
     * API-Suche als Observable für switchMap – bei neuem Suchwert wird der vorherige
     * Request ignoriert (kein Überschreiben durch späte Antworten).
     */
    private getSearchRequest(value: string): Observable<{ records?: any[] }> {
        let params = this.params ?? new HttpParams();
        params = params.set('search', value ?? '');

        if (this.limit) {
            params = params.set('limit', this.limit.toString());
        }

        let url: string;
        if (this.genericApiUrl) {
            if (
                this.genericApiUrl.startsWith('http://') ||
                this.genericApiUrl.startsWith('https://')
            ) {
                url = this.genericApiUrl;
            } else {
                url = this.apiBaseUrl + this.genericApiUrl;
            }
        } else {
            url = this.apiUrl + 'read.php';
        }

        return this.api.get(url, params);
    }

    mapper(ko?: any) {
        if (!ko) {
            return '';
        }

        if (typeof ko === 'string') {
            return ko;
        }

        if (this.mapperDisplayFields.length > 0) {
            let field = '';
            for (const d of this.mapperDisplayFields) {
                if (ko[d]) {
                    field += ko[d] + ' ';
                }
            }
            return field;
        } else if (this.displayFields.length > 0) {
            let field = '';
            for (const d of this.displayFields) {
                if (ko[d]) {
                    field += ko[d] + ' ';
                }
            }
            return field;
        }

        return ko[this.displayField];
    }

    emitSelectedElement(event: MatAutocompleteSelectedEvent) {
        const elem = event.option.value;
        if (elem && elem !== '') {
            this.optionSelected = true;
        }
        this.elementSelected.emit(elem);
        // Keep behavior deterministic in dialogs: close panel immediately after a pick.
        this.auto?.closePanel();
        this.activatedObjectSelected = false;
        this.suppressNextAutoOpenAfterSelect = true;
        if (this.clearAfterSelect && elem !== '') {
            this.formControl.setValue(null, { emitEvent: false });
        }
    }

    setActivatedObject(event: MatAutocompleteActivatedEvent) {
        this.activatedObject = event.option.value;
        this.activatedObjectSelected = true;
    }

    onBlur() {
        if (this.emitFreeText && !this.optionSelected) {
            this.emitTypedText();
        }
        if (this.activatedObjectSelected) {
            this.elementSelected.emit(this.activatedObject);
            this.activatedObjectSelected = false;
        }
    }

    onFocus() {
        if (this.suppressNextAutoOpenAfterSelect) {
            this.suppressNextAutoOpenAfterSelect = false;
            if (this.isObject(this.formControl.value)) {
                return;
            }
        }
        if (this.openOnlyOnClick && !this.openPanelAfterPointer) {
            return;
        }
        this.openPanelAfterPointer = false;
        this.loadOptionsAndOpenPanel(false);
    }

    onFormFieldPointerDown(event: PointerEvent): void {
        if (!this.openOnlyOnClick) {
            return;
        }
        const target = event.target as HTMLElement | null;
        if (target?.closest('.mat-autocomplete-panel') || target?.closest('button')) {
            return;
        }
        this.openPanelAfterPointer = true;
    }

    onInputClick() {
        this.suppressNextAutoOpenAfterSelect = false;
        if (this.openOnlyOnClick) {
            this.openPanelAfterPointer = true;
        }
        this.loadOptionsAndOpenPanel(true);
    }

    private loadOptionsAndOpenPanel(forceOpen: boolean) {
        let value = this.formControl.value;
        if (this.isObject(value)) {
            value = '';
        }
        this.isLoading = true;
        this.getSearchRequest(value || '')
            .pipe(
                finalize(() => (this.isLoading = false)),
                catchError(err => {
                    this.messageService.errorMessage('Es ist ein Fehler aufgetreten!', err);
                    return of({ records: [] });
                }),
            )
            .subscribe({
                next: response => {
                    this.options = response?.records ?? [];
                    this.cdr.detectChanges();
                    this.openPanelIfReady(forceOpen);
                },
            });
    }

    private openPanelIfReady(forceOpen: boolean): void {
        if (!this.options?.length) {
            return;
        }
        const shouldOpen = forceOpen || this.openPanelAfterPointer || !!this.inputAutocomplete?.focused;
        this.openPanelAfterPointer = false;
        if (!shouldOpen) {
            return;
        }
        queueMicrotask(() => this.auto?.openPanel());
    }

    isObject(obj: any): boolean {
        const type = typeof obj;
        return type === 'function' || (type === 'object' && !!obj);
    }

    getValue(obj: any): string {
        if (this.displayFields.length > 0) {
            let s = '';
            let i = 0;
            for (const df of this.displayFields) {
                const txt = obj[df];
                if (txt) {
                    if (i > 0) {
                        s += this.separator;
                    }
                    s += txt;
                    i++;
                }
            }
            return s;
        } else {
            return obj[this.displayField];
        }
    }

    setError(error: string) {
        this.formControl.markAsTouched();
        this.formControl.setErrors({ [error]: true });
    }

    isValid() {
        this.formControl.markAsTouched();
        return this.formControl.valid;
    }

    // von außen
    public emitCurrentValue() {
        const typedValue = this.formControl.value;
        if (typedValue) {
            this.elementSelected.emit(typedValue);
            this.optionSelected = false;
            if (this.clearAfterSelect) {
                this.formControl.setValue(null);
            }
        }
    }

    public emitTypedText() {
        const typedValue = this.formControl.value;
        if (typedValue !== undefined && typedValue !== null) {
            this.elementSelected.emit(typedValue);
            this.optionSelected = false;
            if (this.clearAfterSelect) {
                this.clear();
            }
        }
    }

    emitCreateNewObj() {
        // this.setCreateObj();
        this.auto.closePanel();
        this.createNewEmitter.emit(this.createObj);
    }

    setCreateObj() {
        this.createObj = this.inputAutocomplete.value + '';
    }

    checkForKeyShortcut(event: KeyboardEvent) {
        if (event.altKey && event.code === 'Enter') {
            this.emitCreateNewObj();
        }
    }

    closePanel() {
        this.auto.closePanel();
    }
}
